
import { Component, Vue, Prop } from 'vue-property-decorator';
import { sendOtp } from '@/api/login_utils';
import ApplicationState from '@/business/models/application_state';
import BigCookie from '@/business/models/big_cookie';
import Customer from '@/business/models/customer';
import MtProfileBuilder from '@/business/mt_profile_builder';
import bus from '@/lib/event_bus';
import { get, isEmpty } from '@/lib/helpers';
import { getNavigationRoute, reroute } from '@/lib/layout_config';
import {
  isCustomerAuthenticated,
  isCustomerAuthenticatedSync,
} from '@/lib/login_utils';
import { ErrorBag, validate, validateAll } from '@/lib/validator';
import {
  captureConsent,
  doLogin,
  refreshAccessToken,
  storeAccessDetails,
  updateSession,
} from '@/store/actions/auth';
import { retrieveBigCookie } from '@/store/actions/customer';
import { layoutConfig } from '@/store/getters/app_config';
import { authState } from '@/store/getters/auth';
import {
  decodeJsonWebToken,
  getAllCookies,
  isMicroappsEnabled,
  loadGoogleRecaptcha,
  uuid,
} from '../lib/utils';
import { defaultCatch, responseHelper } from '@/api/utils';
import {
  captureTimelineEvent,
  saveInviteCode,
  updateEmail,
  updateMobile,
  validateInviteCode,
} from '@/api';
import { setLocalKey } from '@/store/local_store';
import { ACCESS_DETAILS } from '@/store/store_keys';
import LoginScreen from '@/components/LoginScreen.vue';
import { store } from '@/store/index';
import PhoneVerification from '@/components/PhoneVerification.vue';
declare const google: any;

@Component({
  components: {
    'error-holder': () => import('@/components/qual/ErrorHolder.vue'),
    LoginScreen,
    'phone-verification': PhoneVerification, //() => import('@/components/PhoneVerification.vue'),
    'email-verification': () => import('@/components/EmailVerification.vue'),
  },
})
export default class CustomerLogin extends Vue {
  @Prop() private qualStrings!: any;

  private phoneNumber: string = '';
  private otp: string = '';
  private authType: string = 'OTP';
  private userConsentAndTnc: boolean = true;
  private count: number = 30;
  private otpButtonMessage: string = 'Resend OTP';
  private errors: ErrorBag = {};
  private whatsAppOptIn: boolean = true;
  private showOtpScreen: boolean = false;
  private otpParts: string[] = ['', '', '', ''];
  private disabledButton: boolean = false;
  private otpSent: boolean = false;
  private inviteCodeMessage: string = 'Apply Code';
  private inviteCodeError: string = '';
  private inviteCode: string = '';
  private validInviteCode: boolean = false;
  private indexValue = 0;
  private autoSlideTimer: any = null;
  private state: any = store.getState();

  private showEmailVerification: boolean = false;
  private showPhoneNumberScreen: boolean = false;
  private isEmailVerified: boolean = false;
  private isPhoneVerified: boolean = false;
  private emailId: string = '';
  private isSendingOtp: boolean = false;

  private showResendOtp: boolean = true;

  private get strings() {
    return {
      pageTitle: this.qualStrings.submitApplication.pageTitle,
      inputs: this.qualStrings.inputs,
      errors: this.qualStrings.errors,
      notifications: this.qualStrings.notifications,
      loadingMsgs: this.qualStrings.loadingMessages,
      userConsentAndTnc:
        this.qualStrings.submitApplication.userConsentAndTncObj,
    };
  }

  private get isMicroappsEnabled() {
    return isMicroappsEnabled();
  }

  private get isGoogleSigninEnabled() {
    const cookies = getAllCookies();
    const utmSource = cookies.utm_source || this.$route.query.utm_source || '';
    return (
      process.env.VUE_APP_GOOGLE_SIGNIN_ENABLED === 'true' &&
      process.env.VUE_APP_GSIGN_UTM_SOURCES &&
      process.env.VUE_APP_GSIGN_UTM_SOURCES.split(',').includes(utmSource)
    );
  }

  private get customerData() {
    const isCustomerAuthenticated = isCustomerAuthenticatedSync(authState());
    if (isCustomerAuthenticated) {
      if (!isEmpty(this.state.customer)) {
        return this.state.customer;
      }
      return {};
    }
    return null;
  }

  private get validations() {
    return [
      {
        id: 'phone_number',
        name: 'Phone Number',
        rules: 'required|min:10',
        value: this.phoneNumber,
        regex: /^[0-9]{10}$/,
        messages: {
          required: 'Phone number is required',
          regex: 'Enter a valid phone number',
        },
      },
      {
        id: 'OTP',
        name: 'OTP',
        rules: 'required|integer',
        value: this.otp,
        messages: {
          required: 'OTP is required',
        },
      },
    ];
  }

  public async created() {
    if (process.env.NODE_ENV === 'local') {
      const authStr = process.env.VUE_APP_AUTH_TOKEN;
      console.log(authStr);
      setLocalKey(ACCESS_DETAILS, authStr);
      storeAccessDetails({ payload: JSON.parse(authStr || '{}') });
      updateSession({ payload: { session_id: uuid() } });
    }
    if (isMicroappsEnabled()) {
      this.$router.replace({
        name: get(
          layoutConfig,
          'GPAY_LAYOUT.routes.login',
          'gpay-customerLogin'
        ),
        query: this.$route.query,
      });
    }
    const phone = get(this.$route, 'query.phone', '');
    this.phoneNumber = phone;
    const isAuthenticated: boolean = await isCustomerAuthenticated(authState());
    loadGoogleRecaptcha();
    if (isAuthenticated) {
      this.fetchUserDetailsAndLogin(() => {
        // On initial failure use refresh token to update access token
        // TODO: Handle this scenario more wisely
        return;
        // eslint-disable-next-line no-unreachable
        const refreshToken = get(authState(), 'refresh_token', '');
        if (!isEmpty(refreshToken)) {
          refreshAccessToken(refreshToken)
            .then((success) => {
              if (success) {
                this.fetchUserDetailsAndLogin(this.emitAutoLoginFailure);
              } else {
                this.emitAutoLoginFailure();
              }
            })
            .catch(() => {
              this.emitAutoLoginFailure();
            });
          // eslint-disable-next-line no-unreachable
        } else {
          this.emitAutoLoginFailure();
        }
      });
    }

    if (!this.isGoogleSigninEnabled) {
      this.showPhoneNumberScreen = true;
    }
    bus.$on('gmail-signin-response', this.handleCredentialResponse);
  }

  private mounted() {
    this.autoSlideImages();
  }

  public async fetchUserDetailsAndLogin(callback: () => void) {
    try {
      const response: any = await retrieveBigCookie(
        true,
        'CustomerLogin:fetchUserDetailsAndLogin'
      );
      const customer: Customer = Customer.getInstance();
      bus.$emit('syncEvent', 'WEB.AUTO_LOGIN.SUCCESS', {
        phone: customer.phone || '',
        emailId: customer.emailId || '',
        nonInteraction: true,
      });
      this.isEmailVerified = response.emailVerified;
      this.isPhoneVerified = response.phoneVerified;
      if (
        (this.isEmailVerified || !this.isGoogleSigninEnabled) &&
        this.isPhoneVerified
      ) {
        bus.$emit('notify', {
          type: 'success',
          title: 'Success!',
          message: 'You are now logged in',
          autoClose: 3000,
          showClose: true,
        });
        this.$nextTick(() => {
          this.redirect();
        });
      } else if (
        !this.isEmailVerified &&
        this.isPhoneVerified &&
        this.isGoogleSigninEnabled
      ) {
        this.googleSignIn();
      } else if (
        (this.isEmailVerified || !this.isGoogleSigninEnabled) &&
        !this.isPhoneVerified
      ) {
        this.openPhoneNumberScreen();
      }
    } catch (err) {
      loadGoogleRecaptcha();
      callback();
    }
  }

  public redirect(captureLogin = false) {
    const ctxApplicationState: ApplicationState =
      BigCookie.getInstance().applicationState;
    const loanResponse = BigCookie.getInstance().loanResponseDetails;
    this.$nextTick(() => {
      MtProfileBuilder.getInstance().buildApplicationDetails();
    });
    const redirectRouteName = get(this.$route, 'query.redirect', '');
    let navigationRoute = null;
    if (redirectRouteName) {
      navigationRoute = getNavigationRoute(
        ctxApplicationState,
        loanResponse,
        redirectRouteName
      );
    } else {
      navigationRoute = getNavigationRoute(ctxApplicationState, loanResponse);
    }
    navigationRoute.query = { ...navigationRoute.query, ...this.$route.query };

    const { targetRoute, query, params } = navigationRoute;
    const sessionId = uuid();
    updateSession({ payload: { session_id: sessionId } });
    const cookies = getAllCookies();
    const customerId = BigCookie.getInstance().customerId;
    bus.$emit('capture-id', customerId);
    if (
      captureLogin &&
      cookies.utm_source &&
      cookies.utm_source !== process.env.VUE_APP_ID
    ) {
      const {
        utm_source: utmSource = '',
        utm_medium: utmMedium = '',
        utm_campaign: utmCampaign = '',
        utm_content: utmContent = '',
      } = cookies;

      captureTimelineEvent({
        event: 'MT_WEBQUAL_UTM_LOGIN',
        category: 'UTM_TRACKING',
        utmParameters: {
          ...(utmSource && { utm_source: utmSource }),
          ...(utmCampaign && { utm_campaign: utmCampaign }),
          ...(utmMedium && { utm_medium: utmMedium }),
          ...(utmContent && { utm_content: utmContent }),
          sessionId,
        },
      })
        .then(
          responseHelper(
            (response: any) =>
              console.log(`Event captured: MT_WEBQUAL_UTM_LOGIN: `, response),
            (err: any) => console.log(err)
          )
        )
        .catch((err: any) =>
          console.error('Error capturing event: MT_WEBQUAL_UTM_LOGIN: ', err)
        );
    }

    reroute(targetRoute, query, params);
  }

  private openPhoneNumberScreen() {
    this.showPhoneNumberScreen = true;
  }

  private googleSignIn() {
    this.showOtpScreen = false;
    this.showEmailVerification = true;
    const googleBtn = this.$el.querySelector('#google-signin-button');
    google.accounts.id.renderButton(googleBtn, {
      theme: 'outline',
      size: 'large',
      shape: 'circle',
      logo_alignment: 'left',
      width: '200',
    });
  }

  public emitAutoLoginFailure() {
    bus.$emit('syncEvent', 'WEB.AUTO_LOGIN.FAILURE', { nonInteraction: true });
    bus.$emit('notify', {
      type: 'warning',
      message: 'Seems like you were on vacation for a while. Login again.',
      autoClose: 4000,
      showClose: true,
    });
  }

  public sendOtp(data: any) {
    this.phoneNumber = data.phoneNumber;
    this.inviteCode = data.inviteCode;
    this.whatsAppOptIn = data.whatsAppOptIn;
    this.validateField('phone_number');
    if (
      !this.disabledButton &&
      (!this.errors.phone_number || isEmpty(this.errors.phone_number))
    ) {
      bus.$emit('syncEvent', 'WEB.LOGIN_OTP.REQUEST', {
        phone: this.phoneNumber,
      });
      if ((window as any).grecaptcha && !this.isSendingOtp) {
        this.isSendingOtp = true;
        (window as any).grecaptcha.ready(() => {
          (window as any).grecaptcha
            .execute(process.env.VUE_APP_RECAPTCHA, { action: 'auth_qual_2' })
            .then((token: any) => {
              this.otpParts = ['', '', '', ''];
              sendOtp({
                phone: this.phoneNumber,
                purpose: 'PH_VERIFICATION',
                token,
              })
                .then(() => {
                  this.disabledButton = true;
                  this.showPhoneNumberScreen = false;
                  this.showOtpScreen = true;
                  this.startCountDown();
                  bus.$emit('syncEvent', 'WEB.LOGIN_OTP.SEND_SUCCESS', {
                    phone: this.phoneNumber,
                    nonInteraction: true,
                  });
                  bus.$emit('notify', {
                    type: 'success',
                    message: 'OTP sent successfully',
                    autoClose: 4000,
                    showClose: true,
                  });
                  const otpInput: any =
                    this.$el.querySelector('.input[name=otp]');
                  if (otpInput) {
                    otpInput.focus();
                  }
                  setTimeout(() => {
                    this.disabledButton = false;
                    this.otpButtonMessage = 'Resend OTP';
                  }, 30000);
                })
                .catch((error: any) => {
                  this.disabledButton = false;
                  this.otpButtonMessage = 'Resend OTP';
                  bus.$emit('syncEvent', 'WEB.LOGIN_OTP.SEND_FAILURE', {
                    phoneNumber: this.phoneNumber,
                    error,
                    nonInteraction: true,
                  });
                  const errorCode = get(error, 'data.error.code');
                  let errorMessage = 'OTP not sent. Try again';
                  if (errorCode === 477) {
                    errorMessage = get(error, 'data.error.message');
                  }
                  bus.$emit('notify', {
                    type: 'error',
                    message: errorMessage,
                    showClose: true,
                    autoClose: 4000,
                  });
                })
                .finally(() => (this.isSendingOtp = false));
            });
        });
      } else {
        try {
          window.navigator.vibrate([100, 10, 200]);
        } catch {
          console.log('Error while calling vibrate');
        }
      }
    }
  }

  public async login(otp: string) {
    this.otp = otp;
    validateAll(this.validations).then(async (errors: ErrorBag) => {
      if (!errors) {
        this.isPhoneVerified = true;
        try {
          if (!this.isEmailVerified) {
            const response: any = await doLogin({
              userName: this.phoneNumber,
              password: this.otp,
              authType: this.authType,
            });
            if (response.authenticated) {
              captureConsent({
                tcAccepted: this.userConsentAndTnc,
                tcAcceptedAt: new Date().toISOString(),
                whatsAppOptIn: this.whatsAppOptIn ? 'YES' : 'NO',
                privacyPolicyAccepted: this.userConsentAndTnc,
                privacyPolicyAcceptedAt: new Date().toISOString(),
              });
              bus.$emit('syncEvent', 'WEB.LOGIN.SUCCESS', {
                phone: this.phoneNumber,
              });
            }
          }
        } catch (err) {
          bus.$emit('syncEvent', 'WEB.LOGIN.FAILURE', {
            phone: this.phoneNumber,
          });
          console.log(err);
        } finally {
          bus.$emit('loader');
        }
        const isAuthenticated = await isCustomerAuthenticated(authState());
        if (isAuthenticated) {
          try {
            this.showResendOtp = false;
            bus.$emit('loader', {
              show: true,
              message: 'Getting your details...',
            });
            if (this.isEmailVerified && this.isGoogleSigninEnabled) {
              try {
                await updateMobile({ phone: this.phoneNumber, otp: this.otp });
              } catch (err) {
                console.log('Error while updating phone number: ', err);
                defaultCatch(err, { action: 'signing in with phone' });
                bus.$emit('syncEvent', 'WEB.LOGIN.FAILURE', {
                  phone: this.phoneNumber,
                });
                return;
              }
            }
            const response: any = await retrieveBigCookie(
              true,
              'CustomerLogin:login'
            );
            bus.$emit('loader', {
              show: true,
              message: 'Reading your details...',
            });
            if (!response.emailVerified && this.isGoogleSigninEnabled) {
              this.googleSignIn();
            }
            if (this.validInviteCode && this.inviteCode) {
              this.updateInviteCode(
                BigCookie.getInstance().customerId,
                this.inviteCode
              );
            }
            // bus.$emit('loader');
            if (response.emailVerified || !this.isGoogleSigninEnabled) {
              this.redirect();
            }
          } catch (err) {
            bus.$emit('syncEvent', 'WEB.CUSTOMER_DATA.RETRIEVAL_FAILURE', {
              phone: this.phoneNumber,
              nonInteraction: true,
            });
            console.log(err);
            this.showResendOtp = true;
            this.showOtpScreen = false;
            this.showPhoneNumberScreen = true;
            bus.$emit('loader');
          } finally {
            bus.$emit('loader');
          }
        } else {
          this.showResendOtp = true;
          this.showOtpScreen = false;
          this.showPhoneNumberScreen = true;
          this.disabledButton = false;
          bus.$emit('syncEvent', 'WEB.SESSION.EXPIRED', {
            phone: this.phoneNumber,
            nonInteraction: true,
          });
          this.$router.push({
            name: get(layoutConfig(), 'routes.customerLogin', 'customerLogin'),
            query: this.$route.query,
          });
        }
      } else {
        this.errors = errors;
        window.navigator.vibrate([100, 10, 200]);
        (this.$refs.OTP as any).focus();
      }
    });
  }

  private async handleCredentialResponse(gmailSigninResponse: any) {
    if (gmailSigninResponse.success) {
      const decodedJWT = await decodeJsonWebToken(gmailSigninResponse.jwt);
      if (!isEmpty(decodedJWT)) {
        const userName = decodedJWT.email;
        this.emailId = userName;
        this.isEmailVerified = true;
        try {
          if (!this.isPhoneVerified) {
            await doLogin({
              userName: userName,
              password: gmailSigninResponse.jwt,
              authType: 'GMAIL',
            });
          }
        } catch (err) {
          bus.$emit('syncEvent', 'WEB.GMAIL.LOGIN_FAILURE', {
            userName,
            nonInteraction: true,
          });
          console.log(err);
        } finally {
          bus.$emit('loader');
        }

        const isAuthenticated = await isCustomerAuthenticated(authState());
        if (isAuthenticated) {
          try {
            bus.$emit('loader', {
              show: true,
              message: 'Getting your details...',
            });
            if (this.isPhoneVerified) {
              try {
                await updateEmail({
                  username: userName,
                  password: gmailSigninResponse.jwt,
                  authType: 'GMAIL',
                });
                bus.$emit('syncEvent', 'WEB.GMAIL.LOGIN', {
                  userName,
                  nonInteraction: true,
                });
              } catch (err) {
                console.log('Error while updating email: ', err);
                defaultCatch(err, { action: 'signing in with gmail' });
                bus.$emit('syncEvent', 'WEB.GMAIL.LOGIN_FAILURE', {
                  userName,
                  nonInteraction: true,
                });
                return;
              }
            }
            const response: any = await retrieveBigCookie(
              true,
              'CustomerLogin:login'
            );
            if (!response.phoneVerified) {
              this.showPhoneNumberScreen = true;
            }
            bus.$emit('loader');
            if (response.phoneVerified) {
              this.redirect();
            }
          } catch (err) {
            bus.$emit('syncEvent', 'WEB.CUSTOMER_DATA.RETRIEVAL_FAILURE', {
              emailId: this.emailId,
              nonInteraction: true,
            });
            console.log(err);
            bus.$emit('loader');
          } finally {
            bus.$emit('loader');
          }
        } else {
          bus.$emit('syncEvent', 'WEB.SESSION.EXPIRED', {
            phone: this.phoneNumber,
            nonInteraction: true,
          });
          this.$router.push({
            name: get(layoutConfig(), 'routes.customerLogin', 'customerLogin'),
            query: this.$route.query,
          });
        }
      }
    }
  }

  private reroute(routeName: string) {
    const currentRouteName: string = this.$route.name || '';
    if (currentRouteName !== routeName) {
      this.$router.push({ name: routeName });
    }
  }

  private startCountDown() {
    let time: any;
    if (this.count > 0 && this.disabledButton) {
      this.disabledButton = true;
      this.count = this.count - 1;
      this.otpButtonMessage = `Resend OTP in 00: ${String(this.count).padStart(
        2,
        '0'
      )}`;
      time = setTimeout(() => this.startCountDown(), 1000);
    } else {
      this.disabledButton = false;
      clearTimeout(time);
      this.count = 30;
    }
  }

  private validateField(fieldName: string) {
    for (const validation of this.validations) {
      if (validation.id === fieldName) {
        this.$set(this.errors, validation.id, validate(validation));
      }
    }
  }

  private resetOtpScreen() {
    this.showOtpScreen = false;
    this.showPhoneNumberScreen = true;
    this.disabledButton = false;
  }

  private submitInviteCode() {
    if (this.inviteCodeMessage === 'Apply Code') {
      this.validateCode();
    } else {
      this.inviteCode = '';
      this.inviteCodeMessage = 'Apply Code';
    }
  }

  private validateCode() {
    // TODO: Implementation subject to change when appvirality comes into play
    this.inviteCodeError = '';
    if (this.inviteCode) {
      bus.$emit('syncEvent', 'WEB.PROMO_CODE.VALIDATE_START', {
        inviteCode: this.inviteCode,
        phone: this.phoneNumber,
      });
      validateInviteCode({ inviteCode: this.inviteCode })
        .then(
          responseHelper(
            (response: any) => {
              const { remarks: message = 'Coupon applied successfully' } =
                response;
              bus.$emit('syncEvent', 'WEB.PROMO_CODE.VALIDATE_SUCCESS', {
                inviteCode: this.inviteCode,
                phone: this.phoneNumber,
                nonInteraction: true,
              });
              bus.$emit('notify', {
                type: 'success',
                message,
                title: 'Coupon applied!',
              });
              this.validInviteCode = true;
              this.inviteCodeMessage = 'Change';
            },
            (errorCode: string, errorMessage: string) => {
              this.inviteCodeError =
                errorMessage || "Invite code couldn't be applied";
            }
          )
        )
        .catch((error: any) => {
          const errorCode = get(error, 'data.error.code');
          bus.$emit('syncEvent', 'WEB.PROMO_CODE.VALIDATE_ERROR', {
            inviteCode: this.inviteCode,
            phone: this.phoneNumber,
            nonInteraction: true,
          });
          if (errorCode === 477) {
            const errorMessage = get(error, 'data.error.message');
            this.inviteCodeError =
              errorMessage || "Invite code couldn't be applied";
          } else {
            this.inviteCodeError = "Invite code couldn't be applied";
          }
          this.validInviteCode = false;
        });
    } else {
      this.inviteCodeError = 'Please enter a valid invite code';
    }
  }

  private updateInviteCode(customerId: string, inviteCode: string) {
    saveInviteCode(customerId, inviteCode)
      .then((response: any) => {
        if (response.ok) {
          bus.$emit('syncEvent', 'WEB.PROMO_CODE.SAVE_SUCCESS', {
            phone: get(BigCookie.getInstance(), 'customerDetails.phone', ''),
            nonInteraction: true,
          });
        }
      })
      .catch(defaultCatch);
  }

  private autoSlideImages() {
    this.autoSlideTimer = setTimeout(this.autoSlideImages, 3000);
    const radioBtnElems: any = document.querySelectorAll(
      'input[name="slider"]'
    );
    if (!radioBtnElems || radioBtnElems.length <= 0) {
      clearTimeout(this.autoSlideTimer);
    } else {
      this.indexValue++;
      if (this.indexValue > radioBtnElems.length) {
        this.indexValue = 1;
      }
      radioBtnElems[this.indexValue - 1].checked = true;
    }
  }

  private async checkAndRedirect() {
    const isAuthenticated = await isCustomerAuthenticated(authState());
    if (this.isPhoneVerified && isAuthenticated) {
      this.redirect();
    } else {
      this.showEmailVerification = false;
      this.showPhoneNumberScreen = false;
    }
  }

  private beforeDestroy() {
    clearTimeout(this.autoSlideTimer);
    const recaptchaBadge: any = document.querySelector('.grecaptcha-badge');
    if (recaptchaBadge) {
      recaptchaBadge.style.display = 'none';
    }
  }
}
